// Revisado: 2025-12-24 [I18n Scan]
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../../constants';

// Helper: Tie-breaker logic (SSR > SR > R)
const getRarityWeight = (rarity) => {
    if (rarity === 'SSR') return 3;
    if (rarity === 'SR') return 2;
    if (rarity === 'R') return 1;
    return 0;
};

// Helper: Sort cards by value then rarity
const sortBest = (cardList, getValueFn) => {
    return [...cardList].sort((a, b) => {
        const valA = getValueFn(a);
        const valB = getValueFn(b);
        if (valB !== valA) return valB - valA; // Higher value wins

        // Tie-breaker: Rarity
        const rarA = getRarityWeight(a.info.rarity);
        const rarB = getRarityWeight(b.info.rarity);
        return rarB - rarA; // Higher rarity wins
    });
};

const SupportAnalytics = ({ cards }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    const analysis = useMemo(() => {
        if (!cards || cards.length === 0) return null;

        const results = {};

        // Helper to format
        const fmt = (val) => {
            if (val === undefined || val === null || isNaN(val)) return '0';
            return Number.isInteger(val) ? val.toString() : val.toFixed(1);
        };

        // 1. Best Cap 0 - 4
        [0, 1, 2, 3, 4].forEach(cap => {
            // Filter out cards where this cap is null (unowned)
            const validCards = cards.filter(c => c.values[cap] !== null);
            const best = sortBest(validCards, c => c.values[cap])[0];
            if (best) results[`best_cap_${cap}`] = { card: best, value: fmt(best.values[cap]) };
        });

        // 2. Best Average (of OWNED caps)
        const bestAvg = sortBest(cards, c => {
            const valid = c.values.filter(v => v !== null); // Ignore nulls
            if (valid.length === 0) return 0;
            return valid.reduce((s, v) => s + v, 0) / valid.length;
        })[0];

        if (bestAvg) {
            const valid = bestAvg.values.filter(v => v !== null);
            const avg = valid.length ? (valid.reduce((s, v) => s + v, 0) / valid.length) : 0;
            results['best_average'] = { card: bestAvg, value: `Avg: ${avg.toFixed(1)}` };
        }

        // 3. Most Consistent (High Average + Low Variance)
        // Score = Average / (1 + StdDev)
        const consistent = sortBest(cards, c => {
            const valid = c.values.filter(v => v !== null);
            if (valid.length < 2) return 0; // Need at least 2 points for variance

            const avg = valid.reduce((s, v) => s + v, 0) / valid.length;
            if (avg === 0) return 0;
            const variance = valid.reduce((s, v) => s + Math.pow(v - avg, 2), 0) / valid.length;
            const stdDev = Math.sqrt(variance);
            return avg / (1 + stdDev);
        })[0];
        if (consistent) results['consistent'] = { card: consistent, label: t('SupportAnalytics.label_stable') };

        // 4. Best Progression (Start OK, End High, Smooth)
        // Requires at least Cap 0 and Max Available Cap
        const progression = sortBest(cards, c => {
            const validIndices = c.values.map((v, i) => v !== null ? i : -1).filter(i => i !== -1);
            if (validIndices.length < 2) return 0; // Need shift

            const firstIdx = validIndices[0];
            const lastIdx = validIndices[validIndices.length - 1];

            const v0 = c.values[firstIdx];
            const vEnd = c.values[lastIdx];

            if (vEnd < v0) return 0; // Must improve

            const validValues = c.values.filter(v => v !== null);
            const avg = validValues.reduce((s, v) => s + v, 0) / validValues.length;
            const variance = validValues.reduce((s, v) => s + Math.pow(v - avg, 2), 0) / validValues.length;

            return (v0 + vEnd) - (variance * 0.5);
        })[0];
        if (progression) results['progression'] = { card: progression, label: t('SupportAnalytics.label_smooth') };

        // 5. F2P (Early Peaker) - Cap 0 vs Max Cap
        const f2p = sortBest(cards, c => {
            const validIndices = c.values.map((v, i) => v !== null ? i : -1).filter(i => i !== -1);
            if (validIndices.length === 0) return 0;

            const firstIdx = validIndices[0];
            const lastIdx = validIndices[validIndices.length - 1];

            // If only 1 cap, ratio is 100% (technically peak performance at start) but trivial.
            // Let's require strictly that vEnd is significant?
            // User logic: "F2P" usually means "Strong at Cap 0".
            // So let's prioritize high Cap 0 value vs End Value ratio.

            const v0 = c.values[firstIdx];
            const vEnd = c.values[lastIdx];

            if (vEnd === 0) return 0;
            return v0 / vEnd; // Higher % is better
        })[0];

        if (f2p) {
            const validIndices = f2p.values.map((v, i) => v !== null ? i : -1).filter(i => i !== -1);
            const first = f2p.values[validIndices[0]];
            const last = f2p.values[validIndices[validIndices.length - 1]];
            const ratio = last ? Math.floor((first / last) * 100) : 100;
            results['f2p'] = { card: f2p, value: `${ratio}% Power at Cap 0` };
        }

        // 6. Power Spike (Largest Jump)
        let bestSpike = { jump: -1, card: null, cap: -1 };
        cards.forEach(c => {
            for (let i = 0; i < 4; i++) {
                // Must have both Caps to measure jump
                if (c.values[i] === null || c.values[i + 1] === null) continue;

                const jump = c.values[i + 1] - c.values[i];
                if (jump > bestSpike.jump) {
                    bestSpike = { jump, card: c, cap: i + 1 };
                } else if (jump === bestSpike.jump && bestSpike.card) {
                    if (getRarityWeight(c.info.rarity) > getRarityWeight(bestSpike.card.info.rarity)) {
                        bestSpike = { jump, card: c, cap: i + 1 };
                    }
                }
            }
        });
        if (bestSpike.card) results['spike'] = { card: bestSpike.card, value: `+${fmt(bestSpike.jump)} at Cap ${bestSpike.cap}` };

        // 7. Late Bloomer (Weak start, strong finish)
        const lateBloomer = sortBest(cards, c => {
            const validIndices = c.values.map((v, i) => v !== null ? i : -1).filter(i => i !== -1);
            if (validIndices.length < 2) return 0;

            const first = c.values[validIndices[0]];
            const last = c.values[validIndices[validIndices.length - 1]];

            return (last - first) * last;
        })[0];
        if (lateBloomer) {
            const validIndices = lateBloomer.values.map((v, i) => v !== null ? i : -1).filter(i => i !== -1);
            const first = lateBloomer.values[validIndices[0]];
            const last = lateBloomer.values[validIndices[validIndices.length - 1]];
            results['late_bloomer'] = { card: lateBloomer, value: t('SupportAnalytics.val_gains', { gain: fmt(last - first) }) };
        }

        // 8. Rarity Bests
        ['SSR', 'SR', 'R'].forEach(r => {
            const bestR = sortBest(cards.filter(c => c.info.rarity === r), c => {
                const valid = c.values.filter(v => v !== null);
                if (!valid.length) return 0;
                return valid.reduce((s, v) => s + v, 0); // Total sum of owned caps
            })[0];
            if (bestR) {
                const valid = bestR.values.filter(v => v !== null);
                const avg = valid.length ? valid.reduce((s, v) => s + v, 0) / valid.length : 0;
                results[`best_${r}`] = { card: bestR, value: `Avg: ${avg.toFixed(1)}` };
            }
        });

        // 9. Perfect Run (Top 10% in ALL OWNED caps)
        // Only valid if card has at least some caps? 
        // Logic: For each cap check threshold.
        const allPerfects = cards.filter(c => {
            // For every cap index...
            // 1. If user doesn't own it (value null), ignore this check? Or is it auto-fail?
            // User wants to see performance of *what they have*. So ignore nulls.
            // But if user has only Cap 0, and they are top 10% in Cap 0, are they Perfect Run? Yes?

            // Check all NON-NULL values
            return c.values.every((v, idx) => {
                if (v === null) return true; // Skip
                if (v === 0) return false; // Zero is not perfect/active

                // Get threshold for this idx based on ALL cards that have this idx
                const validValuesForIdx = cards.map(x => x.values[idx]).filter(x => x !== null).sort((a, b) => b - a);
                const top10Index = Math.floor(validValuesForIdx.length * 0.1);
                const threshold = validValuesForIdx[top10Index] || 0;
                return v >= threshold;
            });
        });

        const bestPerfect = sortBest(allPerfects, c => {
            const valid = c.values.filter(v => v !== null);
            return valid.reduce((s, v) => s + v, 0);
        })[0];


        if (bestPerfect) results['perfect_run'] = { card: bestPerfect, label: 'Top 10% All Owned' };

        return results;

    }, [cards, t]);

    if (!cards || cards.length === 0) return null;

    // Helper to render a card block
    const CardBlock = ({ title, data, color }) => {
        if (!data) return null;
        const { card, value, label } = data;

        // Define Icon Path (Copied from Table)
        const typeStr = card.info.Support_Type;
        let typeIcon = '';
        if (typeStr) {
            const t = typeStr.charAt(0).toUpperCase() + typeStr.slice(1).toLowerCase();
            if (['Speed', 'Stamina', 'Power', 'Guts', 'Wit'].includes(t)) {
                typeIcon = `/images/Icon/Stats_icons/Stat_Type_${t}.png`;
            } else if (['Pal', 'Group'].includes(t)) {
                typeIcon = `/images/Icon/Support_Icons/Support_Type_${t}.png`;
            }
        }

        return (
            <div className={`relative bg-slate-800 rounded-lg p-3 border border-slate-700 shadow-md flex items-center gap-3 overflow-hidden group hover:bg-slate-750 transition-colors`}>
                <div className={`absolute top-0 right-0 px-2 py-0.5 text-[10px] font-bold uppercase rounded-bl-lg ${color || 'bg-slate-700 text-slate-300'}`}>
                    {title}
                </div>

                {/* Image */}
                <div className="relative w-12 h-12 flex-shrink-0 mt-2">
                    <img
                        src={`${API_BASE_URL}/api/thumb?img=/images/Icon/Support_Character_Icon/Support_Card_${card.id}_Icon.png&width=64`}
                        className="w-full h-full rounded shadow-sm object-cover"
                        alt={card.info.name}
                        loading="lazy"
                    />
                    <img
                        src={typeIcon}
                        className="absolute -bottom-1 -right-1 w-5 h-5 z-10 drop-shadow-md"
                        alt={card.info.Support_Type}
                    />
                </div>

                <div className="min-w-0 flex-1 mt-2">
                    <div className="font-bold text-xs truncate text-cyan-100">
                        {card.info.char_name || card.info.name}
                    </div>
                    <div className="text-[10px] text-slate-400 truncate">
                        {card.info.card_name}
                    </div>
                    <div className="text-[10px] font-mono text-amber-300">
                        {value || label}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-lg overflow-hidden transition-all">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex justify-between items-center p-4 bg-slate-50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-slate-800 dark:text-white">
                        <span className="text-purple-600 dark:text-purple-400">{t('SupportAnalytics.title')}</span>
                    </h3>
                    <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300 rounded-full border border-purple-200 dark:border-purple-800">
                        {t('SupportAnalytics.badge_ai')}
                    </span>
                </div>
                <span className={`transform transition-transform ${isOpen ? 'rotate-180' : ''} text-slate-400`}>
                    ▼
                </span>
            </button>

            {isOpen && analysis && (
                <div className="p-6 bg-slate-50 dark:bg-slate-900/30 border-t border-slate-200 dark:border-slate-700 space-y-8">

                    {/* 1. Best Caps */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 pb-2">
                            Best Caps
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                            {[0, 1, 2, 3, 4].map(cap => (
                                <CardBlock
                                    key={cap}
                                    title={`Best Cap ${cap}`}
                                    data={analysis[`best_cap_${cap}`]}
                                    color="bg-blue-900/80 text-blue-200"
                                />
                            ))}
                        </div>
                    </div>

                    {/* 2. Best Rarity */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 pb-2">
                            Best by Rarity
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <CardBlock title="Best SSR" data={analysis['best_SSR']} color="bg-gradient-to-r from-yellow-600 to-yellow-800 text-white" />
                            <CardBlock title="Best SR" data={analysis['best_SR']} color="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black" />
                            <CardBlock title="Best R" data={analysis['best_R']} color="bg-slate-600 text-white" />
                        </div>
                    </div>

                    {/* 3. Best Categories */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 pb-2">
                            {t('SupportAnalytics.header_performance')}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <CardBlock title={t('SupportAnalytics.card_highest_avg')} data={analysis['best_average']} color="bg-emerald-900/80 text-emerald-200" />
                            <CardBlock title={t('SupportAnalytics.card_consistent')} data={analysis['consistent']} color="bg-teal-900/80 text-teal-200" />
                            <CardBlock title={t('SupportAnalytics.card_progression')} data={analysis['progression']} color="bg-indigo-900/80 text-indigo-200" />
                        </div>
                    </div>

                    {/* 4. Analyze */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 pb-2">
                            {t('SupportAnalytics.header_analysis')}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <CardBlock title={t('SupportAnalytics.card_f2p')} data={analysis['f2p']} color="bg-cyan-900/80 text-cyan-200" />
                            <CardBlock title={t('SupportAnalytics.card_spike')} data={analysis['spike']} color="bg-rose-900/80 text-rose-200" />
                            <CardBlock title={t('SupportAnalytics.card_late_bloomer')} data={analysis['late_bloomer']} color="bg-orange-900/80 text-orange-200" />
                        </div>
                    </div>

                    {/* 5. PERFECT RUN (TIER S) */}
                    {analysis['perfect_run'] && (
                        <div className="relative group animate-fade-in-up">
                            {/* Golden Glow Effect */}
                            <div className="absolute -inset-1 bg-gradient-to-r from-yellow-300 via-amber-500 to-yellow-300 rounded-xl blur opacity-25 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
                            <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-amber-500/50 rounded-xl p-6 flex flex-col items-center text-center shadow-2xl">
                                <h4 className="text-2xl font-black italic text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-400 to-amber-200 mb-4 animate-shine">
                                    ✨ PERFECT RUN (TIER S) ✨
                                </h4>
                                <div className="w-full max-w-md">
                                    <CardBlock
                                        title="Top 10% Across All Caps"
                                        data={analysis['perfect_run']}
                                        color="bg-yellow-500/20 text-yellow-200 border border-yellow-500/50"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            )}
        </div>
    );
};

export default SupportAnalytics;
